import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authJsonHeaders } from '../utils/authHeaders';
import {
  buildCaisseYearOptions,
  getActiveCaisseYear,
  getCalendarYear,
  setActiveCaisseYear,
  subscribeCaisseYear,
  syncActiveCaisseYearWithCalendar,
} from '../utils/caisseActiveYear';
import CabinetEditForm from './CabinetEditForm';
import CabinetsAdminShell from './CabinetsAdminShell';
import './CabinetsSelectPage.css';

export default function CabinetsSelectPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAdmin, activeCabinet, setActiveCabinet } = useAuth();
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editCabinet, setEditCabinet] = useState(null);
  const [deleteCabinet, setDeleteCabinet] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeNav, setActiveNav] = useState('liste');
  const [activeCaisseYear, setActiveCaisseYearState] = useState(() =>
    syncActiveCaisseYearWithCalendar()
  );
  const yearOptions = useMemo(() => buildCaisseYearOptions(), [activeCaisseYear]);

  const canManage = isAdmin();

  useEffect(() => {
    return subscribeCaisseYear((y) => setActiveCaisseYearState(y));
  }, []);

  const handleCaisseYearChange = (e) => {
    const y = setActiveCaisseYear(parseInt(e.target.value, 10));
    setActiveCaisseYearState(y);
  };

  const loadCabinets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/cabinets', { headers: authJsonHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Impossible de charger les cabinets');
      }
      setCabinets(data.cabinets || []);
    } catch (e) {
      setError(e.message || 'Erreur chargement cabinets');
      setCabinets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCabinets();
  }, [loadCabinets]);

  // Caissier (User) : pas de page de choix — entrée directe dans sa caisse
  useEffect(() => {
    if (loading || canManage) return;
    if (cabinets.length >= 1) {
      const preferred =
        (activeCabinet?.id && cabinets.find((c) => Number(c.id) === Number(activeCabinet.id))) ||
        cabinets[0];
      setActiveCabinet(preferred);
      navigate('/', { replace: true });
    }
  }, [loading, canManage, cabinets, activeCabinet, setActiveCabinet, navigate]);

  // Ajouter un cabinet depuis une autre page admin via navigate state
  useEffect(() => {
    if (!canManage || loading) return;
    const st = location.state || {};
    if (st.openAddCabinet) {
      setShowAdd(true);
      setActiveNav('ajouter');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [canManage, loading, location.state, location.pathname, navigate]);

  const enterCabinet = (cab) => {
    setActiveCabinet(cab);
    navigate('/', { replace: true });
  };

  const handleCabinetCreated = async () => {
    setShowAdd(false);
    setActiveNav('liste');
    await loadCabinets();
  };

  const handleCabinetUpdated = async (updated) => {
    setEditCabinet(null);
    if (updated?.id && activeCabinet?.id && Number(updated.id) === Number(activeCabinet.id)) {
      setActiveCabinet({ ...activeCabinet, ...updated });
    }
    await loadCabinets();
  };

  const handleConfirmDelete = async () => {
    if (!deleteCabinet?.id || deleting) return;
    setDeleting(true);
    setError('');
    try {
      const res = await fetch(`/api/cabinets/${deleteCabinet.id}`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Suppression impossible');
      }
      if (activeCabinet?.id && Number(activeCabinet.id) === Number(deleteCabinet.id)) {
        setActiveCabinet(null);
      }
      setDeleteCabinet(null);
      await loadCabinets();
    } catch (err) {
      setError(err.message);
      setDeleteCabinet(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleAjouterCabinet = () => {
    setActiveNav('ajouter');
    setShowAdd(true);
  };

  const content = (
    <>
      <div className="cabinets-select-topbar">
        <div className="cabinets-year-select" role="group" aria-label="Année d'exercice">
          <label htmlFor="cabinets-caisse-year">Année</label>
          <select
            id="cabinets-caisse-year"
            value={activeCaisseYear}
            onChange={handleCaisseYearChange}
            title={
              activeCaisseYear === getCalendarYear()
                ? `Année en cours (${getCalendarYear()})`
                : `Consultation ${activeCaisseYear} · calendrier ${getCalendarYear()}`
            }
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
                {y === getCalendarYear() ? ' (en cours)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="cabinets-select-header-actions">
          {activeCabinet ? (
            <button
              type="button"
              className="cabinets-btn cabinets-btn--ghost"
              onClick={() => navigate('/')}
            >
              Retour ({activeCabinet.name})
            </button>
          ) : null}
          {!canManage ? (
            <button
              type="button"
              className="cabinets-btn cabinets-btn--danger"
              onClick={() => {
                logout();
                navigate('/');
              }}
            >
              Déconnexion
            </button>
          ) : null}
          {canManage ? (
            <button
              type="button"
              className="cabinets-btn cabinets-btn--success"
              onClick={handleAjouterCabinet}
            >
              <i className="fas fa-plus" aria-hidden="true" /> Ajouter cabinet
            </button>
          ) : null}
        </div>
      </div>

      {error ? <div className="cabinets-alert">{error}</div> : null}

      {loading || (!canManage && cabinets.length >= 1) ? (
        <div className="cabinets-loading">Ouverture de la caisse…</div>
      ) : cabinets.length === 0 ? (
        <div className="cabinets-empty">
          Aucun cabinet assigné. Demandez à un administrateur de vous donner un point
          d&apos;accès.
        </div>
      ) : (
        <div className="cabinets-grid">
          {cabinets.map((cab) => {
            const isDefault = Number(cab.is_default) === 1;
            return (
              <article
                key={cab.id}
                className={`cabinet-card${isDefault ? ' cabinet-card--default' : ''}`}
                role="button"
                tabIndex={0}
                aria-label={`Accéder à la caisse ${cab.name || ''}`}
                onClick={() => enterCabinet(cab)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    enterCabinet(cab);
                  }
                }}
              >
                <div className="cabinet-card__glow" aria-hidden="true" />
                <div className="cabinet-card__top">
                  <div
                    className={`cabinet-card__badge${
                      isDefault ? ' cabinet-card__badge--live' : ' cabinet-card__badge--new'
                    }`}
                  >
                    {isDefault ? 'Données actuelles' : 'Nouveau / vide'}
                  </div>
                  {canManage ? (
                    <div className="cabinet-card__icons">
                      <button
                        type="button"
                        className="cabinet-card__edit"
                        title="Modifier le cabinet"
                        aria-label="Modifier le cabinet"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditCabinet(cab);
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="cabinet-card__delete"
                        title={
                          isDefault
                            ? 'Le cabinet principal ne peut pas être supprimé'
                            : 'Supprimer le cabinet'
                        }
                        aria-label="Supprimer le cabinet"
                        disabled={isDefault}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteCabinet(cab);
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="cabinet-card__icon-wrap" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                    <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
                  </svg>
                </div>

                <h2>{cab.name}</h2>
                <p className="cabinet-card__code">{cab.code || '—'}</p>
                {cab.member_role ? (
                  <p className="cabinet-card__role">
                    <span className="cabinet-card__role-pill">{cab.member_role}</span>
                  </p>
                ) : null}

                <div className="cabinet-card__actions">
                  <button
                    type="button"
                    className="cabinets-btn cabinets-btn--primary cabinet-card__enter"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      enterCabinet(cab);
                    }}
                  >
                    Accéder à la caisse
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {showAdd ? (
        <div className="cabinets-modal-overlay" onClick={() => setShowAdd(false)}>
          <div
            className="cabinets-modal cabinets-modal--edit"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              <i className="fas fa-building me-2" aria-hidden="true" />
              Nouveau cabinet
            </h3>
            <p className="cabinets-modal-hint">
              Le cabinet démarre vide (navbar / pages visibles, sans données). Remplissez les infos
              cabinet, administration et contact.
            </p>
            <CabinetEditForm
              mode="create"
              onSaved={handleCabinetCreated}
              onCancel={() => setShowAdd(false)}
            />
          </div>
        </div>
      ) : null}

      {editCabinet ? (
        <div className="cabinets-modal-overlay" onClick={() => setEditCabinet(null)}>
          <div
            className="cabinets-modal cabinets-modal--edit"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              <i className="fas fa-building me-2" aria-hidden="true" />
              Modifier le cabinet
            </h3>
            <CabinetEditForm
              cabinet={editCabinet}
              onSaved={handleCabinetUpdated}
              onCancel={() => setEditCabinet(null)}
            />
          </div>
        </div>
      ) : null}

      {deleteCabinet ? (
        <div
          className="cabinets-modal-overlay"
          onClick={() => !deleting && setDeleteCabinet(null)}
        >
          <div className="cabinets-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Supprimer le cabinet</h3>
            <p className="cabinets-modal-hint">
              Confirmer la suppression de <strong>{deleteCabinet.name}</strong> ? Les points
              d&apos;accès associés seront retirés.
            </p>
            <div className="cabinets-modal-actions">
              <button
                type="button"
                className="cabinets-btn cabinets-btn--ghost"
                onClick={() => setDeleteCabinet(null)}
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                type="button"
                className="cabinets-btn cabinets-btn--danger"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Suppression…' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </>
  );

  if (canManage) {
    return (
      <CabinetsAdminShell activeNav={activeNav} onAjouterCabinet={handleAjouterCabinet}>
        {content}
      </CabinetsAdminShell>
    );
  }

  return <div className="cabinets-select-page">{content}</div>;
}
